import { supabase } from '../lib/supabase'

export interface AnalyticsOverview {
  totalViews: number
  totalWatchTime: number // in seconds
  completionRate: number // percentage
  activeClients: number
  totalVideos: number
  totalLikes: number
  totalComments: number
}

export interface VideoPerformance {
  id: string
  title: string
  thumbnail_url: string | null
  view_count: number
  like_count: number
  comment_count: number
  created_at: string
}

export interface ViewsOverTime {
  date: string
  views: number
}

export interface ClientActivity {
  date: string
  activeClients: number
  completions: number
}

// ============================================
// Compliance Tracking Types & Functions
// ============================================

export type ComplianceStatus = 'on_track' | 'behind' | 'at_risk' | 'completed'

export interface ComplianceOverview {
  totalActiveClients: number
  totalActiveAssignments: number
  overallComplianceRate: number
  onTrackCount: number
  behindCount: number
  atRiskCount: number
  completedCount: number
}

export interface ClientCompliance {
  clientId: string
  clientName: string
  clientEmail: string
  assignmentId: string
  programId: string
  programTitle: string
  assignedAt: string
  expiresAt: string | null
  totalTasks: number
  completedTasks: number
  progressPercent: number
  lastActivity: string | null
  status: ComplianceStatus
  currentDay?: number
  currentWeek?: number
}

export interface ProgramCompliance {
  programId: string
  programTitle: string
  totalClients: number
  avgComplianceRate: number
  onTrackCount: number
  behindCount: number
  atRiskCount: number
}

export const analyticsService = {
  /**
   * Get overview analytics for a provider
   */
  async getOverview(providerId: string): Promise<AnalyticsOverview> {
    // Get video stats
    const { data: videos, error: videosError } = await (supabase
      .from('videos')
      .select('id, view_count, like_count, comment_count')
      .eq('user_id' as any, providerId) as any)

    if (videosError) throw videosError

    // Type assertion needed until database types are regenerated
    const videosData = videos as { id: string; view_count: number | null; like_count: number | null; comment_count: number | null }[] | null

    const totalViews = videosData?.reduce((sum, v) => sum + (v.view_count || 0), 0) || 0
    const totalLikes = videosData?.reduce((sum, v) => sum + (v.like_count || 0), 0) || 0
    const totalComments = videosData?.reduce((sum, v) => sum + (v.comment_count || 0), 0) || 0
    const totalVideos = videosData?.length || 0

    // Get watch time from video_views
    const videoIds = videosData?.map(v => v.id) || []
    let totalWatchTime = 0
    let totalCompleted = 0
    let totalViewRecords = 0

    if (videoIds.length > 0) {
      const { data: viewData, error: viewError } = await (supabase
        .from('video_views')
        .select('view_duration_seconds, completed')
        .in('video_id' as any, videoIds) as any)

      if (!viewError && viewData) {
        const typedViewData = viewData as { view_duration_seconds: number | null; completed: boolean }[]
        totalWatchTime = typedViewData.reduce((sum, v) => sum + (v.view_duration_seconds || 0), 0)
        totalCompleted = typedViewData.filter(v => v.completed).length
        totalViewRecords = typedViewData.length
      }
    }

    const completionRate = totalViewRecords > 0
      ? Math.round((totalCompleted / totalViewRecords) * 100)
      : 0

    // Get active clients count
    const { count: activeClients } = await (supabase
      .from('clients')
      .select('*', { count: 'exact', head: true })
      .eq('provider_id' as any, providerId)
      .eq('status' as any, 'active') as any)

    return {
      totalViews,
      totalWatchTime,
      completionRate,
      activeClients: activeClients || 0,
      totalVideos,
      totalLikes,
      totalComments,
    }
  },

  /**
   * Get top performing videos
   */
  async getTopVideos(providerId: string, limit = 5): Promise<VideoPerformance[]> {
    const { data, error } = await (supabase
      .from('videos')
      .select('id, title, thumbnail_url, view_count, like_count, comment_count, created_at')
      .eq('user_id' as any, providerId)
      .order('view_count', { ascending: false })
      .limit(limit) as any)

    if (error) throw error
    // Type assertion needed until database types are regenerated
    return (data as VideoPerformance[]) || []
  },

  /**
   * Get views over time (last 30 days)
   */
  async getViewsOverTime(providerId: string, days = 30): Promise<ViewsOverTime[]> {
    // First get the provider's video IDs
    const { data: videos } = await (supabase
      .from('videos')
      .select('id')
      .eq('user_id' as any, providerId) as any)

    const typedVideos = videos as { id: string }[] | null
    const videoIds = typedVideos?.map(v => v.id) || []

    if (videoIds.length === 0) {
      return generateEmptyDates(days)
    }

    // Get views grouped by date
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const { data: viewData, error } = await (supabase
      .from('video_views')
      .select('created_at')
      .in('video_id' as any, videoIds)
      .gte('created_at', startDate.toISOString()) as any)

    if (error) throw error

    // Aggregate by date
    const viewsByDate: Record<string, number> = {}

    // Initialize all dates with 0
    for (let i = 0; i < days; i++) {
      const date = new Date()
      date.setDate(date.getDate() - (days - 1 - i))
      const dateStr = date.toISOString().split('T')[0]
      viewsByDate[dateStr] = 0
    }

    // Count views per date
    const typedViewData = viewData as { created_at: string }[] | null
    typedViewData?.forEach(view => {
      const dateStr = view.created_at.split('T')[0]
      if (viewsByDate[dateStr] !== undefined) {
        viewsByDate[dateStr]++
      }
    })

    return Object.entries(viewsByDate).map(([date, views]) => ({
      date,
      views,
    }))
  },

  /**
   * Get client activity over time
   */
  async getClientActivity(providerId: string, days = 30): Promise<ClientActivity[]> {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    // Get assignment progress data
    const { data: assignments } = await (supabase
      .from('assignments')
      .select('id')
      .eq('provider_id' as any, providerId) as any)

    const typedAssignments = assignments as { id: string }[] | null
    const assignmentIds = typedAssignments?.map(a => a.id) || []

    const activityByDate: Record<string, { activeClients: Set<string>, completions: number }> = {}

    // Initialize all dates
    for (let i = 0; i < days; i++) {
      const date = new Date()
      date.setDate(date.getDate() - (days - 1 - i))
      const dateStr = date.toISOString().split('T')[0]
      activityByDate[dateStr] = { activeClients: new Set(), completions: 0 }
    }

    if (assignmentIds.length > 0) {
      const { data: progressData } = await (supabase
        .from('assignment_progress')
        .select('client_id, completed_at, last_watched_at')
        .in('assignment_id' as any, assignmentIds)
        .gte('last_watched_at', startDate.toISOString()) as any)

      const typedProgressData = progressData as { client_id: string; completed_at: string | null; last_watched_at: string | null }[] | null
      typedProgressData?.forEach(progress => {
        if (progress.last_watched_at) {
          const dateStr = progress.last_watched_at.split('T')[0]
          if (activityByDate[dateStr]) {
            activityByDate[dateStr].activeClients.add(progress.client_id)
          }
        }
        if (progress.completed_at) {
          const dateStr = progress.completed_at.split('T')[0]
          if (activityByDate[dateStr]) {
            activityByDate[dateStr].completions++
          }
        }
      })
    }

    return Object.entries(activityByDate).map(([date, data]) => ({
      date,
      activeClients: data.activeClients.size,
      completions: data.completions,
    }))
  },

  /**
   * Get recent activity feed
   */
  async getRecentActivity(providerId: string, limit = 10) {
    const { data: videos } = await (supabase
      .from('videos')
      .select('id')
      .eq('user_id' as any, providerId) as any)

    const typedVideos = videos as { id: string }[] | null
    const videoIds = typedVideos?.map(v => v.id) || []

    if (videoIds.length === 0) {
      return []
    }

    // Get recent views
    const { data: recentViews } = await (supabase
      .from('video_views')
      .select(`
        id,
        created_at,
        completed,
        video:videos(title)
      `)
      .in('video_id' as any, videoIds)
      .order('created_at', { ascending: false })
      .limit(limit) as any)

    const typedRecentViews = recentViews as { id: string; created_at: string; completed: boolean; video: { title: string } | null }[] | null
    return typedRecentViews?.map(view => ({
      type: 'view' as const,
      id: view.id,
      timestamp: view.created_at,
      completed: view.completed,
      videoTitle: view.video?.title || 'Unknown Video',
    })) || []
  },

  // ============================================
  // Compliance Tracking Functions
  // ============================================

  /**
   * Get compliance overview stats for a provider
   */
  async getComplianceOverview(providerId: string): Promise<ComplianceOverview> {
    // Get all active assignments with progress
    const clientData = await this.getClientCompliance(providerId)

    const totalActiveClients = new Set(clientData.map(a => a.clientId)).size
    const totalActiveAssignments = clientData.length

    // Calculate overall compliance (average progress)
    const overallComplianceRate = clientData.length > 0
      ? Math.round(clientData.reduce((sum, a) => sum + a.progressPercent, 0) / clientData.length)
      : 0

    // Count by status
    const statusCounts = clientData.reduce((acc, a) => {
      acc[a.status] = (acc[a.status] || 0) + 1
      return acc
    }, {} as Record<ComplianceStatus, number>)

    return {
      totalActiveClients,
      totalActiveAssignments,
      overallComplianceRate,
      onTrackCount: statusCounts.on_track || 0,
      behindCount: statusCounts.behind || 0,
      atRiskCount: statusCounts.at_risk || 0,
      completedCount: statusCounts.completed || 0,
    }
  },

  /**
   * Get per-client compliance data
   */
  async getClientCompliance(
    providerId: string,
    filters?: { programId?: string; status?: ComplianceStatus }
  ): Promise<ClientCompliance[]> {
    // Get active assignments with client and program info
    let query = supabase
      .from('assignments')
      .select(`
        id,
        client_id,
        program_id,
        assigned_at,
        expires_at,
        status,
        client:clients (id, full_name, email),
        program:programs (id, title, organization_type)
      `)
      .eq('provider_id' as any, providerId)
      .eq('status' as any, 'active')
      .order('assigned_at', { ascending: false }) as any

    if (filters?.programId) {
      query = query.eq('program_id' as any, filters.programId)
    }

    const { data: assignments, error } = await query

    if (error) throw error
    if (!assignments || assignments.length === 0) return []

    // Get program items count for each program
    const programIds = [...new Set(assignments.map((a: any) => a.program_id))]
    const { data: programItems } = await (supabase
      .from('program_items')
      .select('program_id')
      .in('program_id' as any, programIds) as any)

    const itemCountByProgram = (programItems || []).reduce((acc: Record<string, number>, item: any) => {
      acc[item.program_id] = (acc[item.program_id] || 0) + 1
      return acc
    }, {})

    // Get assignment progress
    const assignmentIds = assignments.map((a: any) => a.id)
    const { data: progressData } = await (supabase
      .from('assignment_progress')
      .select('assignment_id, completed_at, last_watched_at')
      .in('assignment_id' as any, assignmentIds)
      .not('completed_at', 'is', null) as any)

    // Count completed items per assignment
    const completedByAssignment = (progressData || []).reduce((acc: Record<string, { count: number; lastActivity: string | null }>, p: any) => {
      if (!acc[p.assignment_id]) {
        acc[p.assignment_id] = { count: 0, lastActivity: null }
      }
      acc[p.assignment_id].count++
      const currentActivity = acc[p.assignment_id].lastActivity
      if (!currentActivity || (p.last_watched_at && p.last_watched_at > currentActivity)) {
        acc[p.assignment_id].lastActivity = p.last_watched_at || p.completed_at
      }
      return acc
    }, {})

    // Map to ClientCompliance
    const results: ClientCompliance[] = assignments.map((a: any) => {
      const totalTasks = itemCountByProgram[a.program_id] || 0
      const completedInfo = completedByAssignment[a.id] || { count: 0, lastActivity: null }
      const completedTasks = completedInfo.count
      const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0

      // Calculate expected progress based on days since assignment
      const assignedDate = new Date(a.assigned_at)
      const today = new Date()
      const daysSinceAssigned = Math.floor((today.getTime() - assignedDate.getTime()) / (1000 * 60 * 60 * 24)) + 1

      // Determine status based on progress vs expected
      let status: ComplianceStatus
      if (progressPercent >= 100) {
        status = 'completed'
      } else if (totalTasks === 0) {
        status = 'on_track'
      } else {
        // Expected progress: assume daily tasks
        const expectedProgress = Math.min(100, Math.round((daysSinceAssigned / totalTasks) * 100))
        const progressDiff = progressPercent - expectedProgress

        if (progressDiff >= -10) {
          status = 'on_track'
        } else if (progressDiff >= -30) {
          status = 'behind'
        } else {
          status = 'at_risk'
        }
      }

      return {
        clientId: a.client.id,
        clientName: a.client.full_name || 'Unknown',
        clientEmail: a.client.email,
        assignmentId: a.id,
        programId: a.program.id,
        programTitle: a.program.title,
        assignedAt: a.assigned_at,
        expiresAt: a.expires_at,
        totalTasks,
        completedTasks,
        progressPercent,
        lastActivity: completedInfo.lastActivity,
        status,
        currentDay: daysSinceAssigned,
      }
    })

    // Filter by status if specified
    if (filters?.status) {
      return results.filter(r => r.status === filters.status)
    }

    return results
  },

  /**
   * Get compliance data grouped by program
   */
  async getProgramCompliance(providerId: string): Promise<ProgramCompliance[]> {
    const clientData = await this.getClientCompliance(providerId)

    // Group by program
    const byProgram = clientData.reduce((acc, a) => {
      if (!acc[a.programId]) {
        acc[a.programId] = {
          programId: a.programId,
          programTitle: a.programTitle,
          clients: [],
        }
      }
      acc[a.programId].clients.push(a)
      return acc
    }, {} as Record<string, { programId: string; programTitle: string; clients: ClientCompliance[] }>)

    // Calculate stats per program
    return Object.values(byProgram).map(p => {
      const statusCounts = p.clients.reduce((acc, a) => {
        acc[a.status] = (acc[a.status] || 0) + 1
        return acc
      }, {} as Record<ComplianceStatus, number>)

      const avgCompliance = p.clients.length > 0
        ? Math.round(p.clients.reduce((sum, a) => sum + a.progressPercent, 0) / p.clients.length)
        : 0

      return {
        programId: p.programId,
        programTitle: p.programTitle,
        totalClients: p.clients.length,
        avgComplianceRate: avgCompliance,
        onTrackCount: statusCounts.on_track || 0,
        behindCount: statusCounts.behind || 0,
        atRiskCount: statusCounts.at_risk || 0,
      }
    })
  },
}

/**
 * Generate empty date entries for charts
 */
function generateEmptyDates(days: number): ViewsOverTime[] {
  const result: ViewsOverTime[] = []
  for (let i = 0; i < days; i++) {
    const date = new Date()
    date.setDate(date.getDate() - (days - 1 - i))
    result.push({
      date: date.toISOString().split('T')[0],
      views: 0,
    })
  }
  return result
}

/**
 * Format seconds to human readable duration
 */
export function formatWatchTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`
  }
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60)
    return `${mins}m`
  }
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${mins}m`
}
